import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { AlertTriangle, Beaker, Copy, Globe, Key, Power, PowerOff, ShieldAlert, Webhook } from "lucide-react";

const STATE_LABELS = {
  not_configured: { label: "Not configured", tone: "muted" },
  sandbox_ready: { label: "Sandbox ready", tone: "info" },
  awaiting_provider_approval: { label: "Awaiting provider approval", tone: "warn" },
  live_ready: { label: "Live ready", tone: "ok" },
  disabled: { label: "Disabled", tone: "err" },
};

function StateBadge({ state }) {
  const meta = STATE_LABELS[state] || { label: state, tone: "muted" };
  const classes = {
    muted: "bg-slate-100 text-slate-700",
    info: "bg-sky-100 text-sky-800",
    warn: "bg-amber-100 text-amber-900",
    ok: "bg-emerald-100 text-emerald-900",
    err: "bg-rose-100 text-rose-900",
  }[meta.tone];
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${classes}`}>{meta.label}</span>;
}

export default function AdminPaymentSettings() {
  const [providers, setProviders] = useState(null);
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState("sandbox");
  const [credDraft, setCredDraft] = useState({ key_id: "", key_secret: "" });
  const [webhookDraft, setWebhookDraft] = useState("");
  const [notesDraft, setNotesDraft] = useState("");
  const [ops, setOps] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [ledger, setLedger] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/payments/providers");
      setProviders(data);
    } catch (e) {
      toast.error("Could not load providers");
      setProviders({ providers: [] });
    }
  };

  const loadOps = async () => {
    try {
      const { data } = await api.get("/admin/payments/operations?limit=50");
      setOps(data.operations || []);
    } catch { /* best effort */ }
  };
  const loadWebhooks = async () => {
    try {
      const { data } = await api.get("/admin/payments/webhooks?limit=50");
      setWebhooks(data.webhooks || []);
    } catch { /* best effort */ }
  };
  const loadLedger = async () => {
    try {
      const { data } = await api.get("/admin/payments/ledger?limit=100");
      setLedger(data);
    } catch { /* best effort */ }
  };

  useEffect(() => { load(); loadOps(); loadWebhooks(); loadLedger(); }, []);

  const openEdit = (p) => {
    setEditing(p);
    setMode(p.state === "live_ready" ? "live" : "sandbox");
    setCredDraft({ key_id: "", key_secret: "" });
    setWebhookDraft("");
    setNotesDraft(p.notes || "");
  };

  const saveCreds = async () => {
    if (!credDraft.key_id || !credDraft.key_secret) return toast.error("Both key_id and key_secret are required");
    try {
      await api.post(`/admin/payments/providers/${editing.name}/configure`, {
        mode,
        credentials: credDraft,
        notes: notesDraft,
      });
      toast.success(`${mode} credentials saved (masked at rest)`);
      setCredDraft({ key_id: "", key_secret: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const saveWebhook = async () => {
    if (!webhookDraft) return toast.error("Webhook secret required");
    try {
      await api.post(`/admin/payments/providers/${editing.name}/webhook/configure`, { webhook_secret: webhookDraft });
      toast.success("Webhook secret saved");
      setWebhookDraft("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const runTest = async (whichMode) => {
    try {
      const { data: res } = await api.post(`/admin/payments/providers/${editing.name}/test`, { mode: whichMode });
      toast[res.test?.passed ? "success" : "error"](
        `${whichMode} test ${res.test?.passed ? "passed" : "failed"} — ${res.test?.detail || ""}`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  const toggleFlag = async (key, value) => {
    try {
      await api.post(`/admin/payments/providers/${editing.name}/flags`, { [key]: value });
      toast.success(`${key} = ${value}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const toggleDisabled = async (value) => {
    try {
      await api.post(`/admin/payments/providers/${editing.name}/flags`, { disabled: value });
      toast.success(value ? "Provider disabled" : "Provider re-enabled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  if (providers === null) return <Loading />;

  const current = editing && providers.providers.find((p) => p.name === editing.name);

  return (
    <div className="p-6 space-y-6" data-testid="admin-payment-settings">
      <PageHeader
        title="Payment Settings"
        description="Configure the payment provider ahead of go-live. Real customer collections and technician payouts remain disabled until each flag is explicitly turned on."
      />

      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="payments-disabled-banner">
        <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <b>Live payments are disabled.</b> You can configure providers, store sandbox or live credentials, run a self-test and verify webhook signing. Nothing moves money until <code>collections_enabled</code> and <code>payouts_enabled</code> are both on and the booking layer is explicitly wired to this provider — not done today.
        </div>
      </div>

      <Tabs defaultValue="providers">
        <TabsList>
          <TabsTrigger value="providers" data-testid="tab-providers">Providers</TabsTrigger>
          <TabsTrigger value="operations" data-testid="tab-operations">Operations</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks">Webhook log</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Earnings ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="providers" className="space-y-4">
          {providers.providers.map((p) => (
            <Card key={p.provider_id} data-testid={`provider-card-${p.name}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">{p.label} <StateBadge state={p.state} /></CardTitle>
                  <div className="text-xs text-slate-500 mt-1">Capabilities: {p.capabilities.join(", ")}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`provider-open-${p.name}`}>Open</Button>
              </CardHeader>
              <CardContent className="text-sm text-slate-700 space-y-1">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1"><Power className="h-3.5 w-3.5" /> collections: <b>{p.collections_enabled ? "ON" : "OFF"}</b></span>
                  <span className="flex items-center gap-1"><PowerOff className="h-3.5 w-3.5" /> payouts: <b>{p.payouts_enabled ? "ON" : "OFF"}</b></span>
                </div>
                <div className="flex items-center gap-2 text-xs"><Webhook className="h-3.5 w-3.5" /> {p.webhook_url_path} (secret: {p.webhook_secret_masked || "—"})</div>
                <div className="text-xs text-slate-500">
                  sandbox keys: {Object.keys(p.sandbox_credentials_masked).length ? Object.entries(p.sandbox_credentials_masked).map(([k, v]) => `${k}=${v}`).join(" · ") : "—"}
                </div>
                <div className="text-xs text-slate-500">
                  live keys: {Object.keys(p.live_credentials_masked).length ? Object.entries(p.live_credentials_masked).map(([k, v]) => `${k}=${v}`).join(" · ") : "—"}
                </div>
                {p.last_test && (
                  <div className="text-xs text-slate-500">
                    Last test — sandbox: {p.last_test.sandbox_passed ? "✓" : (p.last_test.sandbox_passed === false ? "✗" : "—")} · live: {p.last_test.live_passed ? "✓" : (p.last_test.live_passed === false ? "✗" : "—")}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="operations">
          <Card>
            <CardHeader><CardTitle>Payment / refund / transfer / payout operations</CardTitle></CardHeader>
            <CardContent>
              {ops.length === 0 ? (
                <div className="text-sm text-slate-500">No operations yet. Operations are written only from verified provider webhooks.</div>
              ) : (
                <table className="w-full text-sm border">
                  <thead className="bg-slate-50"><tr><th className="p-2 text-left">When</th><th className="p-2 text-left">Provider</th><th className="p-2 text-left">Type</th><th className="p-2 text-left">Booking</th><th className="p-2 text-left">Ref</th><th className="p-2 text-left">Amount</th></tr></thead>
                  <tbody>
                    {ops.map((o) => (
                      <tr key={o.operation_id} className="border-t">
                        <td className="p-2 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                        <td className="p-2 text-xs">{o.provider}</td>
                        <td className="p-2 text-xs">{o.operation_type}</td>
                        <td className="p-2 text-xs font-mono">{o.booking_id || "—"}</td>
                        <td className="p-2 text-xs font-mono">{o.provider_ref}</td>
                        <td className="p-2 text-xs">₹{o.amount || "0"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks">
          <Card>
            <CardHeader><CardTitle>Signed webhook log</CardTitle></CardHeader>
            <CardContent>
              {webhooks.length === 0 ? (
                <div className="text-sm text-slate-500">No webhooks received yet.</div>
              ) : (
                <table className="w-full text-sm border">
                  <thead className="bg-slate-50"><tr><th className="p-2 text-left">When</th><th className="p-2 text-left">Provider</th><th className="p-2 text-left">Event</th><th className="p-2 text-left">Event ID</th><th className="p-2 text-left">Verified</th></tr></thead>
                  <tbody>
                    {webhooks.map((w) => (
                      <tr key={w.event_id + w.received_at} className="border-t">
                        <td className="p-2 text-xs">{new Date(w.received_at).toLocaleString()}</td>
                        <td className="p-2 text-xs">{w.provider}</td>
                        <td className="p-2 text-xs">{w.event || "—"}</td>
                        <td className="p-2 text-xs font-mono">{w.event_id}</td>
                        <td className="p-2 text-xs"><Badge variant={w.verified ? "default" : "destructive"}>{w.verified ? "ok" : "rejected"}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ledger">
          <Card>
            <CardHeader><CardTitle>Technician earnings ledger (informational)</CardTitle></CardHeader>
            <CardContent>
              <div className="rounded-md border border-sky-300 bg-sky-50 p-3 text-xs text-sky-900 mb-3">
                Entries are informational only. No real credits, no withdrawals.
              </div>
              {ledger?.totals && Object.keys(ledger.totals).length > 0 && (
                <table className="w-full text-sm border mb-4">
                  <thead className="bg-slate-50"><tr><th className="p-2 text-left">Technician</th><th className="p-2">Commission base</th><th className="p-2">Commission</th><th className="p-2">Service share</th><th className="p-2">Materials</th></tr></thead>
                  <tbody>
                    {Object.entries(ledger.totals).map(([tid, totals]) => (
                      <tr key={tid} className="border-t">
                        <td className="p-2 font-mono text-xs">{tid}</td>
                        <td className="p-2 text-center">₹{totals.commission_base}</td>
                        <td className="p-2 text-center">₹{totals.commission}</td>
                        <td className="p-2 text-center">₹{totals.service_share}</td>
                        <td className="p-2 text-center">₹{totals.materials}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {(!ledger?.entries || ledger.entries.length === 0) ? (
                <div className="text-sm text-slate-500">No ledger entries yet. Entries are written when a pilot booking completes.</div>
              ) : (
                <table className="w-full text-sm border">
                  <thead className="bg-slate-50"><tr><th className="p-2 text-left">When</th><th className="p-2 text-left">Technician</th><th className="p-2 text-left">Booking</th><th className="p-2 text-left">Entry</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">State</th></tr></thead>
                  <tbody>
                    {ledger.entries.map((e) => (
                      <tr key={e.entry_id} className="border-t">
                        <td className="p-2 text-xs">{new Date(e.created_at).toLocaleString()}</td>
                        <td className="p-2 text-xs font-mono">{e.technician_id}</td>
                        <td className="p-2 text-xs font-mono">{e.booking_id}</td>
                        <td className="p-2 text-xs">{e.entry_type}</td>
                        <td className="p-2 text-xs text-right">₹{e.amount}</td>
                        <td className="p-2 text-xs">{e.state}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.label}</DialogTitle></DialogHeader>
          {current && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 text-sm">
                <StateBadge state={current.state} />
                <span className="text-slate-500">{current.webhook_url_path}</span>
              </div>

              <div className="grid gap-3 border rounded-md p-3">
                <div className="font-medium text-sm flex items-center gap-2"><Key className="h-4 w-4" /> Credentials</div>
                <div className="flex gap-2">
                  <Button variant={mode === "sandbox" ? "default" : "outline"} size="sm" onClick={() => setMode("sandbox")} data-testid="pay-mode-sandbox">Sandbox</Button>
                  <Button variant={mode === "live" ? "default" : "outline"} size="sm" onClick={() => setMode("live")} data-testid="pay-mode-live">Live</Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>key_id</Label>
                    <Input value={credDraft.key_id} onChange={(e) => setCredDraft({ ...credDraft, key_id: e.target.value })} placeholder="rzp_test_..." data-testid="pay-cred-key-id" />
                  </div>
                  <div>
                    <Label>key_secret</Label>
                    <Input type="password" value={credDraft.key_secret} onChange={(e) => setCredDraft({ ...credDraft, key_secret: e.target.value })} data-testid="pay-cred-key-secret" />
                  </div>
                </div>
                <Textarea placeholder="Notes (optional)" value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} className="h-16" />
                <div className="flex gap-2">
                  <Button onClick={saveCreds} data-testid="pay-cred-save">Save {mode} credentials</Button>
                  <Button variant="outline" onClick={() => runTest(mode)} data-testid={`pay-test-${mode}`}><Beaker className="h-4 w-4 mr-1" /> Test {mode}</Button>
                </div>
                <div className="text-xs text-slate-500">Current {mode} keys: {Object.entries(mode === "sandbox" ? current.sandbox_credentials_masked : current.live_credentials_masked).map(([k, v]) => `${k}=${v}`).join(" · ") || "—"}</div>
              </div>

              <div className="grid gap-3 border rounded-md p-3">
                <div className="font-medium text-sm flex items-center gap-2"><Webhook className="h-4 w-4" /> Webhook</div>
                <div className="text-xs text-slate-600 flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5" /> URL path: <code>{current.webhook_url_path}</code>
                  <Button variant="ghost" size="icon" onClick={() => navigator.clipboard.writeText(current.webhook_url_path)}><Copy className="h-4 w-4" /></Button>
                </div>
                <div className="text-xs text-slate-600">Current secret: {current.webhook_secret_masked || "—"}</div>
                <div className="flex gap-2">
                  <Input type="password" value={webhookDraft} onChange={(e) => setWebhookDraft(e.target.value)} placeholder="whsec_..." data-testid="pay-webhook-secret" />
                  <Button onClick={saveWebhook} data-testid="pay-webhook-save">Save webhook secret</Button>
                </div>
                <div className="text-xs text-slate-500">We verify HMAC-SHA256 of the canonical JSON body against the <code>X-MSM-Signature</code> header. Duplicate <code>event_id</code>s are deduplicated.</div>
              </div>

              <div className="grid gap-3 border rounded-md p-3">
                <div className="font-medium text-sm flex items-center gap-2"><Power className="h-4 w-4" /> Operational flags</div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm">Customer collections</div>
                    <div className="text-xs text-slate-500">Allow this provider to be used for incoming payments.</div>
                  </div>
                  <Switch checked={current.collections_enabled} onCheckedChange={(v) => toggleFlag("collections_enabled", v)} data-testid="pay-flag-collections" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm">Technician transfers / payouts</div>
                    <div className="text-xs text-slate-500">Allow outbound transfers to technician linked accounts.</div>
                  </div>
                  <Switch checked={current.payouts_enabled} onCheckedChange={(v) => toggleFlag("payouts_enabled", v)} data-testid="pay-flag-payouts" />
                </div>
                <div className="pt-2 border-t">
                  {current.state === "disabled" ? (
                    <Button variant="outline" onClick={() => toggleDisabled(false)} data-testid="pay-enable"><Power className="h-4 w-4 mr-1" /> Re-enable</Button>
                  ) : (
                    <Button variant="destructive" onClick={() => toggleDisabled(true)} data-testid="pay-disable"><PowerOff className="h-4 w-4 mr-1" /> Disable provider</Button>
                  )}
                </div>
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 flex gap-2 items-start">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div>
                  Bookings are never marked paid from a browser redirect alone. The booking layer only recognises a payment after a signed provider webhook arrives on this URL and is verified. Collections and payouts remain OFF globally regardless of this provider's state.
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
